import { Component, OnInit, Injector, OnDestroy } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as _moment from 'moment';

// components
import { FullListComponent } from '../../shared/components/full-list/full-list.component';
import { ReportComponent } from './report.component';

// models
import { Language } from '../../shared/models/language';
import { MasterData } from '../../shared/models/master-data';
import { Product } from '../../shared/models/product';
import { Report } from '../../shared/models/report';

// services
import { LanguageService } from '../../shared/services/language.service';
import { AlertService } from '../../shared/services/alert.service';
import { UserService } from '../../shared/services/user.service';
import { PlatformReportService } from '../shared/services/platform-report.service';
import { ReportingCleanupPayload } from '../shared/services/reporting-cleanup-payload';
import { Moment } from 'moment';

const moment = (_moment as any).default ? (_moment as any).default : _moment;

@Component({
  selector: 'reports-component',
  templateUrl: 'reports.component.html'
})
export class ReportsComponent extends FullListComponent<Report, ReportComponent> implements OnInit, OnDestroy {

  languages: Array<Language> = [];
  products: Array<Product> = [];
  masterDatas: Array<MasterData> = [];
  reports: Array<Report> = [];
  cleanupFrom: Date | null = null;
  cleanupTo: Date | null = null;
  cleanupProductSysName: string | null = null;
  cleanupInProgress = false;
  isSystemUser = false;
  private destroy$ = new Subject<void>();

  constructor(
    protected injector: Injector,
    private languageService: LanguageService,
    private dataService: PlatformReportService,
    private alertService: AlertService,
    private userService: UserService
  ) {
    super(injector, Report);
    this.title.set('REPORTS.TITLE');
  }

  ngOnInit() {
    this.initializeTranslations();
    this.initializeCleanupRange();
    this.checkSystemUser();
    this.getData();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getData() {
    this.spinner.show();
    forkJoin({
      reports: this.dataService.getReports(),
      languages: this.languageService.getLanguages()
    }).subscribe({
      next: result => {
        this.items = result.reports;
        this.items.sort((a, b) => {
          if (a.reportDesign.name < b.reportDesign.name) {
            return -1;
          }
          if (a.reportDesign.name > b.reportDesign.name) {
            return 1;
          }
          return 0;
        });
        this.languages = result.languages;
        this.spinner.hide();
      },
      error: err => {
        this.errorService.show(err);
        this.spinner.hide();
      }
    });
  }

  openReport(reportId: number, auctionClusterId: number) {
      // tslint:disable: no-floating-promises
    if(auctionClusterId !== null) {
      this.router.navigate(['/platform/reports/ac/' + auctionClusterId + '/report/' + reportId]);
    } else {
      this.router.navigate(['/platform/reports/report/' + reportId]);
    }
      // tslint:enable: no-floating-promises
    }

  deleteSelected() {
    this.spinner.show();
    this.dataService.delete(this.itemIdToDelete).subscribe({
      next: () => {
        this.getData();
        this.spinner.hide();
      },
      error: () => {
        this.errorService.show(this.errorService.translations.DELETE_ERROR_MESSAGE);
        this.spinner.hide();
      }
    });
  }

  edit(itemId: number, event: Event) { }

  add() { }

  onCleanupFromChanged(dateTime: Moment | Date) {
    if (!dateTime) {
      this.cleanupFrom = null;
      return;
    }
    this.cleanupFrom = moment(dateTime).toDate();
  }

  onCleanupToChanged(dateTime: Moment | Date) {
    if (!dateTime) {
      this.cleanupTo = null;
      return;
    }
    this.cleanupTo = moment(dateTime).toDate();
  }

  cleanReporting() {
    if (this.cleanupInProgress) {
      return;
    }

    if (!this.isValidDateRange()) {
      this.alertService.alert(this.translate.instant('REPORTS.CLEANUP_INVALID_RANGE'));
      return;
    }

    const payload = this.buildCleanupPayload();
    this.cleanupInProgress = true;
    this.spinner.show();
    this.dataService.cleanupReporting(payload).subscribe({
      next: () => this.onCleanupSuccess(),
      error: err => this.onCleanupError(err)
    });
  }

  private initializeTranslations() {
    this.setTranslations('REPORTS');
  }

  private initializeCleanupRange() {
    this.cleanupFrom = null;
    this.cleanupTo = null;
  }

  private checkSystemUser() {
    this.userService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: user => this.isSystemUser = user?.isSystemUser ?? false,
        error: () => this.isSystemUser = false
      });
  }

  private buildCleanupPayload(): ReportingCleanupPayload {
    const fromValue = this.cleanupFrom ? this.formatDateOnly(this.cleanupFrom) : null;
    const toValue = this.cleanupTo ? this.formatDateOnly(this.cleanupTo) : null;
    const productValue = this.cleanupProductSysName && this.cleanupProductSysName.trim().length > 0
      ? this.cleanupProductSysName.trim()
      : null;

    return {
      from: fromValue,
      to: toValue,
      productSysName: productValue
    };
  }

  private onCleanupSuccess() {
    this.spinner.hide();
    this.cleanupInProgress = false;
    this.alertService.alert(this.translate.instant('REPORTS.CLEANUP_SUCCESS'));
  }

  private onCleanupError(error: any) {
    this.errorService.show(error);
    this.spinner.hide();
    this.cleanupInProgress = false;
  }

  private isValidDateRange(): boolean {
    if (this.cleanupFrom && this.cleanupTo) {
      const fromTime = this.cleanupFrom.getTime();
      const toTime = this.cleanupTo.getTime();
      if (fromTime > toTime) {
        return false;
      }
    }

    return true;
  }

  private formatDateOnly(date: Date): string {
    return moment(date).format('YYYY-MM-DD');
  }
}
